import React, { useEffect, useRef, useState } from "react";
import { Button, Flex, Spin, Typography, message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import AnnouncementBar from "@/components/MarketingPage/AnnouncementBar";
import Header from "@/components/MarketingPage/Header";
import { useConfirmSubscriptionMutation } from "api/pricingApi";
import { useVerifyUserQuery } from "api/authApi";
import cache from "utils/cache";
import { trackMeta, initMetaAdvancedMatching } from "utils/metaPixel";
import { clearSignupProgress } from "utils/signupProgress";
import { Card, PageRoot } from "./styles";

const ThankYouPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmSubscription] = useConfirmSubscriptionMutation();
  const { refetch: refetchUser } = useVerifyUserQuery();
  const isConfirmingRef = useRef(false);
  const [confirmStatus, setConfirmStatus] = useState("idle");

  useEffect(() => {
    const confirmFromRedirect = async () => {
      const params = new URLSearchParams(location.search);
      const sessionId = params.get("session_id");
      const gateway = params.get("gateway");

      if (!sessionId || gateway !== "STRIPE") {
        setConfirmStatus("success");
        return;
      }
      if (isConfirmingRef.current) {
        return;
      }

      const sessionStorageKey = `stripe-subscription-confirmed-${sessionId}`;
      if (sessionStorage.getItem(sessionStorageKey) === "done") {
        setConfirmStatus("success");
        const cleanUrl = `${window.location.origin}/thankyou`;
        window.history.replaceState({}, document.title, cleanUrl);
        return;
      }

      isConfirmingRef.current = true;
      setConfirmStatus("checking");
      sessionStorage.setItem(sessionStorageKey, "processing");

      const response = await confirmSubscription({ sessionId });
      if (response?.error) {
        sessionStorage.removeItem(sessionStorageKey);
        setConfirmStatus("failed");
        message.error(response?.error?.data?.message || "Unable to confirm subscription");
      } else {
        sessionStorage.setItem(sessionStorageKey, "done");
        const userResult = await refetchUser();
        const user = userResult?.data || cache.getItem("user");
        const subscription = user?.subscription;
        const contentName = subscription
          ? `${subscription.planType}_${subscription.plan}`
          : "subscription";
        trackMeta("StartTrial", { content_name: contentName }, sessionId);
        initMetaAdvancedMatching(user);
        clearSignupProgress();
        setConfirmStatus("success");
        const cleanUrl = `${window.location.origin}/thankyou`;
        window.history.replaceState({}, document.title, cleanUrl);
      }
      isConfirmingRef.current = false;
    };

    confirmFromRedirect();
  }, [confirmSubscription, location.search, refetchUser]);

  return (
    <>
      <AnnouncementBar />
      <Header />
      <PageRoot>
        <Flex vertical align="center" justify="center" gap={16}>
          <Card>
            <Typography.Title level={2} style={{ margin: 0, color: "#171725" }}>
              {confirmStatus === "checking"
                ? "Finalizing your subscription..."
                : "Thank you for subscribing!"}
            </Typography.Title>
            <Typography.Paragraph style={{ marginTop: "12px", color: "#585858" }}>
              {confirmStatus === "checking"
                ? "Please wait while we complete your subscription activation."
                : confirmStatus === "failed"
                  ? "We could not verify your subscription from this redirect. Please return to sign-up and try again."
                  : "Your subscription is active now. You can continue exploring products and managing orders."}
            </Typography.Paragraph>
            {confirmStatus === "checking" ? (
              <Spin />
            ) : (
              <Flex gap={12} justify="center" wrap>
                <Button
                  type="primary"
                  onClick={() => navigate("/home")}
                  style={{ background: "#ed6928" }}
                  disabled={confirmStatus === "failed"}
                >
                  {`Go to Dashboard`}
                </Button>
                <Button onClick={() => navigate("/pricing")}>{`Pricing Page`}</Button>
              </Flex>
            )}
          </Card>
        </Flex>
      </PageRoot>
    </>
  );
};

export default ThankYouPage;
